import { useState } from "react";
import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";

import { dayjs } from "@thalamos/common";
import { ResidencyEndReason } from "@thalamos/aspire-common";

import { api } from "~/api.js";
import {
  Button,
  PopupDialog,
  PopupDialogTitle,
  renderErrorToast,
  renderSuccessToast,
} from "~/components/design-system/index.js";

export type RemoveResidentDialogProps = {
  patientId: string;
  residentName: string;
  careHomeName?: string;
  onClose: () => void;
  onRemoved: () => void;
};

/**
 * Removal asks why, because the reasons are not interchangeable: somebody who moved out
 * has a residency that ended, somebody who died has an episode that closed, and a row
 * added in error should never have existed. The record keeps all three distinct rather
 * than collapsing them into a deletion.
 */
const reasons: { value: ResidencyEndReason; label: string; detail: string }[] =
  [
    {
      value: ResidencyEndReason.MovedOut,
      label: "Moved out",
      detail: "Their residency has ended. The assessment history is kept.",
    },
    {
      value: ResidencyEndReason.Deceased,
      label: "Deceased",
      detail: "Closes the episode. The assessment history is kept.",
    },
    {
      value: ResidencyEndReason.RecordedInError,
      label: "Added in error",
      detail: "They were never resident here.",
    },
  ];

export function RemoveResidentDialog(props: RemoveResidentDialogProps) {
  const [reason, setReason] = useState<ResidencyEndReason | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  return (
    <PopupDialog open onClose={props.onClose}>
      <PopupDialogTitle
        closeDialog={props.onClose}
        titleText="Remove resident"
      />
      <Typography sx={{ fontSize: "0.85rem", color: "#54596B", mb: 2 }}>
        {props.careHomeName
          ? `${props.residentName} will no longer appear on the ${props.careHomeName} register.`
          : `${props.residentName} will no longer appear on the register.`}
      </Typography>

      <RadioGroup
        value={reason ?? ""}
        onChange={(event) => {
          setReason(event.target.value as ResidencyEndReason);
        }}
      >
        {reasons.map((option) => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            control={<Radio size="small" />}
            label={
              <Box>
                <Typography sx={{ fontSize: "0.9rem" }}>
                  {option.label}
                </Typography>
                <Typography sx={{ fontSize: "0.75rem", color: "#54596B" }}>
                  {option.detail}
                </Typography>
              </Box>
            }
          />
        ))}
      </RadioGroup>

      <Box
        sx={{ display: "flex", justifyContent: "space-between", mt: 3, gap: 2 }}
      >
        <Button label="Cancel" variant="outlined" onClick={props.onClose} />
        <Button
          label={isSaving ? "Removing..." : "Remove resident"}
          color="error"
          disabled={!reason || isSaving}
          onClick={async () => {
            setIsSaving(true);
            try {
              const response = await api.capacityState.removeResident(
                props.patientId,
                {
                  reason: reason!,
                  effectiveDateTime: dayjs().toISOString(),
                },
              );

              if (response.status >= 400) {
                renderErrorToast({
                  message: "Could not remove this resident",
                });
                return;
              }

              renderSuccessToast({ message: "Resident removed" });
              props.onRemoved();
            } finally {
              setIsSaving(false);
            }
          }}
        />
      </Box>
    </PopupDialog>
  );
}
